import React, { useEffect, useState, useRef } from "react";
import { useAuth } from "@/context/AuthContext";
import { api, API, isPermanenteFout } from "@/lib/api";
import { currentWeek } from "@/lib/week";
import { LoadError, errMessage } from "@/components/LoadError";
import PrepReminder from "@/components/PrepReminder";
import { registerFlush, wacht } from "@/lib/flushRegistry";
import { EerderAangeleverd } from "@/components/voorbereiding/EerderAangeleverd";
import { toast } from "sonner";

const sectionLabel = "text-[11px] font-semibold uppercase tracking-wider text-zinc-400";

let laatsteMount = 0;   // v24-R1 F2: de nieuwste Voorbereiding-pagina; een oude stuurt daarna niets meer

export default function Voorbereiding() {
  const { user } = useAuth();
  const [week, setWeek] = useState(currentWeek());
  const [meta, setMeta] = useState(null);
  const [metaError, setMetaError] = useState("");
  const [loadError, setLoadError] = useState("");
  const [locatie, setLocatie] = useState("");
  const [form, setForm] = useState({ kpis: {}, prep: {}, antwoorden: {}, opmerkingen: {}, eigenPunt: "" });
  // De volledigheid komt van de SERVER (die kent de huidige checks); het scherm rekent niet zelf.
  const [vol, setVol] = useState(null);
  const [checks, setChecks] = useState({ checks: [], nietDezeWeek: [] });
  const [loaded, setLoaded] = useState(false);
  const [status, setStatus] = useState("idle"); // idle | dirty | saving | saved
  const [savedAt, setSavedAt] = useState(null);

  const formRef = useRef(form);
  const dirtyRef = useRef(false);
  const savingRef = useRef(false);
  const timerRef = useRef(null);
  const reeksRef = useRef(0);   // v24 R5: mislukte saves op rij → langer wachten
  const permanentRef = useRef(false);   // v24-R1 F4: de server weigert → niet herhalen tot de volgende wijziging
  const unmountedRef = useRef(false);   // v24-R1 F2: na unmount nooit meer inplannen, één laatste verzending
  const mountIdRef = useRef(0);
  useEffect(() => { unmountedRef.current = false; mountIdRef.current = ++laatsteMount; return () => { unmountedRef.current = true; }; }, []);
  // v24-R1 F2: de pagina is weg → één laatste PUT van het nieuwste formulier (keepalive), een fout geeft een toast.
  const laatsteVerzending = () => {
    const token = localStorage.getItem("tw1_token");
    const ctx = ctxRef.current;
    dirtyRef.current = false;
    const melding = () => toast.error("Je voorbereiding kon niet worden opgeslagen. Open de pagina opnieuw en controleer je antwoorden.");
    // Intussen opnieuw geopend? Dan nooit meer het oude formulier sturen: het zou nieuwer werk overschrijven.
    if (mountIdRef.current !== laatsteMount) { melding(); return; }
    fetch(`${API}/call-prep`, {
      method: "PUT", keepalive: true,
      headers: { "Content-Type": "application/json", ...(token ? { Authorization: `Bearer ${token}` } : {}) },
      body: JSON.stringify({ week: ctx.week, locatie: ctx.locatie, ...formRef.current }),
    }).then((res) => { if (!res.ok) throw new Error(String(res.status)); })
      .catch(melding);
  };
  const ctxRef = useRef({ week, locatie: "" });
  useEffect(() => { formRef.current = form; }, [form]);

  // De doelweek komt van de SERVER: altijd de eerstvolgende call waarvan het venster nog niet gesloten
  // is. Niet meer afhankelijk van de dag waarop je het formulier opent (die bug liet de voorbereiding
  // in een andere week landen dan je weekritme-vinkjes).
  const loadMeta = () => api.get("/meta").then((r) => {
    setMeta(r.data); setMetaError("");
    setWeek(r.data.prepWindow?.week || currentWeek());
    setLocatie(r.data.eigenLocatie || "");
  }).catch((e) => setMetaError(errMessage(e)));

  const loadPrep = (loc) => {
    if (!loc) return;
    setLoaded(false);
    api.get(`/call-prep?week=${week}&locatie=${encodeURIComponent(loc)}`).then((r) => {
      setForm({ kpis: r.data.kpis || {}, prep: r.data.prep || {}, antwoorden: r.data.antwoorden || {},
                opmerkingen: r.data.opmerkingen || {}, eigenPunt: r.data.eigenPunt || "" });
      setVol(r.data.volledigheid || null);
      setChecks({ checks: r.data.checks || [], nietDezeWeek: r.data.nietDezeWeek || [] });
      ctxRef.current = { week, locatie: loc };
      dirtyRef.current = false;
      setLoaded(true); setLoadError(""); setStatus("idle");
    }).catch((e) => setLoadError(errMessage(e)));
  };

  useEffect(() => { loadMeta(); }, []);
  useEffect(() => { if (locatie) loadPrep(locatie); }, [locatie, week]);

  // Save-invariant: alleen ná een geslaagde load (load gate) en alleen bij daadwerkelijke wijziging.
  const doSave = async (silent) => {
    if (!loaded || savingRef.current || !locatie) return;
    const snapshot = formRef.current;
    savingRef.current = true; setStatus("saving");
    try {
      const res = await api.put("/call-prep", { week, locatie, ...snapshot });
      setVol(res.data.volledigheid || null);
      setChecks({ checks: res.data.checks || [], nietDezeWeek: res.data.nietDezeWeek || [] });
      reeksRef.current = 0;
      if (formRef.current === snapshot) { dirtyRef.current = false; setStatus("saved"); setSavedAt(new Date()); }
    } catch (e) {
      reeksRef.current += 1;
      permanentRef.current = isPermanenteFout(e);
      setStatus("dirty");
      if (!silent || permanentRef.current) toast.error(e?.response?.data?.detail || "Opslaan mislukt");
    } finally {
      savingRef.current = false;
      if (dirtyRef.current && loaded) {
        if (unmountedRef.current) laatsteVerzending();   // F2: geen timer op een verdwenen pagina
        else if (!permanentRef.current) scheduleSave(reeksRef.current ? Math.min(1000 * 2 ** (reeksRef.current - 1), 15000) : 1000, true);
      }
    }
  };
  const scheduleSave = (vertraging = 1000, herhaling = false) => {
    if (!loaded || unmountedRef.current) return;
    if (!herhaling) permanentRef.current = false;   // een nieuwe wijziging mag het opnieuw proberen
    dirtyRef.current = true; setStatus("dirty");
    clearTimeout(timerRef.current);
    timerRef.current = setTimeout(() => doSave(true), vertraging);
  };
  const flushNow = () => { clearTimeout(timerRef.current); if (dirtyRef.current && !savingRef.current && !permanentRef.current) doSave(true); };   // v24-R1 F4
  // v17-R1 D4: de herlaadbalk wacht tot alles opgeslagen is.
  const flushWachtRef = useRef(null);
  flushWachtRef.current = async () => {
    clearTimeout(timerRef.current);
    for (let i = 0; i < 50 && savingRef.current; i++) await wacht(100);
    if (dirtyRef.current) await doSave(true);
    for (let i = 0; i < 50 && savingRef.current; i++) await wacht(100);
  };
  useEffect(() => registerFlush(() => flushWachtRef.current()), []);

  // Flush bij tab sluiten/verbergen (keepalive) en bij unmount (navigeren mag niets verliezen).
  useEffect(() => {
    const keepalive = () => {
      if (!dirtyRef.current || !loaded || savingRef.current || !locatie) return;
      clearTimeout(timerRef.current);
      const token = localStorage.getItem("tw1_token");
      try {
        fetch(`${API}/call-prep`, {
          method: "PUT",
          headers: { "Content-Type": "application/json", ...(token ? { Authorization: `Bearer ${token}` } : {}) },
          body: JSON.stringify({ week, locatie, ...formRef.current }),
          keepalive: true,
        });
      } catch { /* ignore */ }
    };
    const onBeforeUnload = () => keepalive();
    const onVis = () => {
      if (document.visibilityState === "hidden") keepalive();
      // v24-R1 F3: terug zichtbaar en nog niet opgeslagen → de herhaling weer starten
      else if (dirtyRef.current && !savingRef.current && !permanentRef.current) scheduleSave(1000, true);
    };
    window.addEventListener("beforeunload", onBeforeUnload);
    document.addEventListener("visibilitychange", onVis);
    return () => {
      window.removeEventListener("beforeunload", onBeforeUnload);
      document.removeEventListener("visibilitychange", onVis);
      clearTimeout(timerRef.current);
      // F2: loopt er een save, dan verstuurt die's finally het nieuwste formulier zodra hij klaar is
      if (dirtyRef.current && loaded && !savingRef.current) laatsteVerzending();
    };
  }, [loaded, locatie]);

  const setKpi = (id, field, val) => { setForm((f) => ({ ...f, kpis: { ...f.kpis, [id]: { ...(f.kpis[id] || { value: "", antwoord: "" }), [field]: val } } })); scheduleSave(); };
  const setPrepCheck = (id, val) => { setForm((f) => ({ ...f, prep: { ...f.prep, [id]: val } })); scheduleSave(); };
  const setAntwoord = (id, val) => { setForm((f) => ({ ...f, antwoorden: { ...(f.antwoorden || {}), [id]: val } })); scheduleSave(); };
  const setOpmerking = (id, val) => { setForm((f) => ({ ...f, opmerkingen: { ...(f.opmerkingen || {}), [id]: val.slice(0, 300) } })); scheduleSave(); };
  const setEigen = (val) => { setForm((f) => ({ ...f, eigenPunt: val })); scheduleSave(); };

  const venster = meta?.prepWindow || null;
  // Venster nog niet open → read-only (de server weigert het toch; het scherm zegt waarom).
  const editable = loaded && !!locatie && !venster?.nogNietOpen;
  const inputCls = "w-full px-3 py-2 rounded-md border border-zinc-300 focus:outline-none focus:ring-2 focus:ring-blue-600 text-sm text-zinc-900 disabled:bg-zinc-50";
  const savedTime = savedAt ? savedAt.toLocaleTimeString("nl-NL", { hour: "2-digit", minute: "2-digit" }) : "";

  return (
    <div data-testid="voorbereiding-page">
      <PrepReminder meta={meta} />
      <div className="mb-3 md:mb-4">
        <h1 className="font-heading text-2xl font-black tracking-tight text-zinc-900">Voorbereiding op de call</h1>
        <p className="text-sm text-zinc-500">
          Voor {locatie ? <span className="font-semibold text-zinc-700">{locatie}</span> : "jouw locatie"}. Zo wordt de call coaching i.p.v. cijfers verzamelen.
        </p>
        {venster && (
          <div data-testid="voorbereiding-doelweek" className={`mt-3 rounded-md border px-3.5 py-2.5 ${venster.nogNietOpen ? "bg-zinc-50 border-zinc-200" : "bg-blue-50 border-blue-200"}`}>
            <p data-testid="voorbereiding-call-kop" className="text-sm font-semibold text-zinc-900">Voorbereiding voor de call van {venster.callMaandagLabel} · cijfers over week {venster.overWeekNr}</p>
            <p className="text-xs text-zinc-500 mt-0.5">
              {venster.nogNietOpen
                ? `Aanleveren kan vanaf ${venster.opensAtLabel} — het venster staat nog niet open.`
                : `Je kunt aanleveren tot ${venster.closesAtLabel} (venster: ${venster.vensterLabel})`}
            </p>
            {vol && vol.totaal > 0 && (
              <p data-testid="voorbereiding-teller-kop" className={`text-xs font-semibold mt-1 ${vol.compleet ? "text-emerald-700" : "text-amber-700"}`}>
                {vol.ingevuld} van {vol.totaal} ingevuld
              </p>
            )}
          </div>
        )}
      </div>

      {(metaError || loadError) && (
        <div className="mb-4 space-y-2">
          <LoadError error={metaError} onRetry={loadMeta} testid="voorbereiding-meta-error" />
          <LoadError error={loadError} onRetry={() => loadPrep(locatie)} testid="voorbereiding-load-error" />
        </div>
      )}

      {(metaError || loadError) ? null : (!meta || !loaded) ? (
        <div data-testid="voorbereiding-loading" className="bg-white rounded-[10px] border border-zinc-200 p-12 text-center text-sm text-zinc-400">Laden…</div>
      ) : (
        <div className="space-y-5 max-w-2xl">
          {editable && (
            <div className="flex justify-end">
              <span data-testid="voorbereiding-autosave" className={`text-[11px] font-medium ${status === "saving" ? "text-blue-600" : status === "saved" ? "text-emerald-600" : status === "dirty" ? "text-zinc-400" : "text-zinc-300"}`}>
                {status === "saving" ? "Bezig met opslaan…" : status === "saved" ? `Opgeslagen ✓ ${savedTime}` : status === "dirty" ? "Niet opgeslagen" : "Automatisch opgeslagen"}
              </span>
            </div>
          )}

          <div className="space-y-3">
            <div className={sectionLabel}>De cijfers</div>
            {(meta.kpis || []).map((kpi) => {
              const v = form.kpis[kpi.id] || { value: "", antwoord: "" };
              return (
                <div key={kpi.id} className="rounded-[10px] border border-zinc-200 p-3 bg-white">
                  <div className="font-heading text-sm font-bold text-zinc-800">{kpi.label}</div>
                  <input disabled={!editable} value={v.value || ""} onChange={(e) => setKpi(kpi.id, "value", e.target.value)} onBlur={flushNow}
                    data-testid={`prep-kpi-value-${kpi.id}`} placeholder="Waarde zoals jij die kent…" className={`${inputCls} mt-1.5`} />
                  <div className="text-xs text-zinc-500 italic mt-2 mb-1">{kpi.vraag}</div>
                  <textarea disabled={!editable} rows={2} value={v.antwoord || ""} onChange={(e) => setKpi(kpi.id, "antwoord", e.target.value)} onBlur={flushNow}
                    data-testid={`prep-kpi-antwoord-${kpi.id}`} placeholder="Jouw antwoord op deze vraag…" className={inputCls} />
                </div>
              );
            })}
          </div>

          <div className="space-y-2">
            <div className="flex items-center justify-between gap-2">
              <div className={sectionLabel}>Voorbereiding &amp; checks</div>
              {vol && vol.totaal > 0 && (
                <span data-testid="voorbereiding-teller" className={`text-xs font-bold tabular-nums px-2 py-0.5 rounded-full border ${vol.compleet ? "bg-emerald-50 text-emerald-700 border-emerald-200" : "bg-amber-50 text-amber-800 border-amber-200"}`}>
                  {vol.ingevuld} van {vol.totaal} ingevuld
                </span>
              )}
            </div>
            <p className="text-xs text-zinc-500 -mt-1">Vul per punt kort in wat je gezien of geregeld hebt — ook als het vinkje uit blijft.</p>
            {checks.checks.map((p) => {
              const antw = (form.antwoorden || {})[p.id] || "";
              const opm = (form.opmerkingen || {})[p.id] || "";
              const leeg = !antw.trim();
              return (
                <div key={p.id} className={`rounded-[10px] border p-2.5 bg-white ${leeg ? "border-amber-300 ring-1 ring-amber-100" : "border-zinc-200"}`}>
                  <label className="flex items-start gap-2.5 cursor-pointer">
                    <input type="checkbox" disabled={!editable} checked={!!form.prep[p.id]} onChange={(e) => setPrepCheck(p.id, e.target.checked)} data-testid={`prep-check-${p.id}`} className="mt-0.5 h-4 w-4 rounded-sm accent-blue-600" />
                    <span className="text-sm text-zinc-700"><span className="text-zinc-400 font-mono text-xs mr-1.5">{p.id}</span>{p.text}{p.label && <span data-testid={`prep-label-${p.id}`} className="ml-2 text-[10px] font-semibold uppercase tracking-wider text-zinc-400">{p.label}</span>}</span>
                  </label>
                  <textarea disabled={!editable} rows={2} value={antw} onChange={(e) => setAntwoord(p.id, e.target.value)} onBlur={flushNow}
                    data-testid={`prep-antwoord-${p.id}`} placeholder="Wat heb je gezien of geregeld? (verplicht)" className={`${inputCls} mt-2`} />
                  {leeg && <p data-testid={`prep-antwoord-nog-${p.id}`} className="text-xs font-medium text-amber-700 mt-1">Nog invullen</p>}
                  <input disabled={!editable} maxLength={300} value={opm} onChange={(e) => setOpmerking(p.id, e.target.value)} onBlur={flushNow}
                    data-testid={`prep-opmerking-${p.id}`} placeholder="Opmerking (optioneel)" className={`${inputCls} mt-1.5`} />
                  <div className="text-[10px] text-zinc-400 text-right mt-0.5" data-testid={`prep-opmerking-teller-${p.id}`}>{opm.length}/300</div>
                </div>
              );
            })}
            {checks.nietDezeWeek.length > 0 && <p data-testid="voorbereiding-niet-deze-week" className="text-xs text-zinc-400">Niet deze week: {checks.nietDezeWeek.map((c) => `${c.text}${c.label ? ` (${c.label})` : ""}`).join(", ")}.</p>}
          </div>

          <div className="space-y-2">
            <div className={sectionLabel}>Wat wil jij zelf inbrengen?</div>
            <textarea disabled={!editable} rows={3} value={form.eigenPunt || ""} onChange={(e) => setEigen(e.target.value)} onBlur={flushNow}
              data-testid="prep-eigen-punt" placeholder="Punten die jij in de call wilt bespreken…" className={inputCls} />
          </div>
        </div>
      )}
      {meta && locatie && <EerderAangeleverd locatie={locatie} kpis={meta.kpis} ververs={savedAt} />}
    </div>
  );
}
